import { AiTool } from '../toolRegistry'
import { richTextFromMarkdown } from '../../lib/rich-text-from-markdown/index'
import { documentToMarkdown } from 'contentful-rich-text-to-markdown'

// Context the entry editor hands to these tools so they stay decoupled from the
// component internals. The editor registers these on mount and unregisters on
// leave, so the AI only has entry-editing tools while an entry is open.
export type EntryToolContext = {
	getEntry: () => any,
	getContentType: () => any,
	getControlsMerged: () => any[],
	getLocaleCodes: () => string[],
	defaultLocale: string,
	setField: (fieldId: string, locale: string, value: any) => void,
}

// Fields a specialised tool owns (e.g. generate_custom_widget owns customCode). The
// generic tools below neither show their values nor write them, so the owning tool
// stays the only way to change them - not just a rule in its prompt.
const claims = new WeakMap<EntryToolContext, Map<string, string>>()

// Returns the release function. `owner` is the name of the tool that owns the fields.
export function claimFields(ctx: EntryToolContext, fieldIds: string[], owner: string): () => void {
	const owned = claims.get(ctx) ?? new Map<string, string>()
	claims.set(ctx, owned)
	for (const fieldId of fieldIds) owned.set(fieldId, owner)
	return () => {
		for (const fieldId of fieldIds) if (owned.get(fieldId) === owner) owned.delete(fieldId)
	}
}

const TEXT_TYPES = [ 'Symbol', 'Text' ]
const NUMBER_TYPES = [ 'Integer', 'Number' ]

// text     -> a single text value (Symbol/Text)
// number   -> a numeric value (Integer/Number)
// boolean  -> a Boolean
// date     -> a Date (ISO string, format per control settings)
// list     -> an Array of text values (Array + items.type Symbol/Text)
// richtext -> a Contentful RichText document (edited as Markdown by the AI)
// other    -> anything the AI can't safely edit yet (links, json, ...)
function fieldKind(field: any): 'text' | 'number' | 'boolean' | 'date' | 'list' | 'richtext' | 'other' {
	if (field.type === 'RichText') return 'richtext'
	if (TEXT_TYPES.includes(field.type)) return 'text'
	if (NUMBER_TYPES.includes(field.type)) return 'number'
	if (field.type === 'Boolean') return 'boolean'
	if (field.type === 'Date') return 'date'
	if (field.type === 'Array' && field.items && TEXT_TYPES.includes(field.items.type)) return 'list'
	return 'other'
}

// A Date field's control decides whether time / timezone are stored.
// 'dateonly' -> YYYY-MM-DD, 'time' -> ...THH:MM:00, else -> ...THH:MM:00±HH:MM
function dateFormat(control: any): 'dateonly' | 'time' | 'full' {
	const f = control?.settings?.format
	if (f === 'dateonly') return 'dateonly'
	if (f === 'time') return 'time'
	return 'full'
}

// Normalize a model-supplied date string into the exact shape DateField expects.
function normalizeDate(input: any, fmt: 'dateonly' | 'time' | 'full'): string {
	const s = String(input ?? '').trim()
	const m = s.match(/^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/)
	if (!m) throw new Error(`"${ input }" is not a valid date. Use YYYY-MM-DD or an ISO date-time.`)
	const date = m[1]
	if (isNaN(new Date(date + 'T00:00:00Z').getTime())) throw new Error(`"${ input }" is not a valid date.`)
	if (fmt === 'dateonly') return date
	const hh = m[2] ?? '00'
	const mm = m[3] ?? '00'
	let value = `${ date }T${ hh }:${ mm }:00`
	if (fmt === 'full') {
		let tz = m[4] ?? '+00:00'
		if (tz === 'Z') tz = '+00:00'
		if (/^[+-]\d{4}$/.test(tz)) tz = tz.slice(0, 3) + ':' + tz.slice(3)
		value += tz
	}
	return value
}

// Enum-style constraints (dropdowns, checkboxes) expose valid options via an
// `in` validation, either on the field itself or on its array items.
function allowedValues(field: any): any[] | null {
	const own = field.validations?.find((v: any) => v.in)?.in
	if (own) return own
	const item = field.items?.validations?.find((v: any) => v.in)?.in
	return item ?? null
}

// Numeric fields can carry a { range: { min, max } } validation.
function numberRange(field: any): { min?: number, max?: number } | null {
	return field.validations?.find((v: any) => v.range)?.range ?? null
}

export function entryToolset(ctx: EntryToolContext): AiTool[] {
	const ownerOf = (fieldId: string) => claims.get(ctx)?.get(fieldId)

	function findControl(fieldId: string) {
		const control = (ctx.getControlsMerged() ?? []).find(c => c.field.id === fieldId)
		if (!control) throw new Error('Unknown field: ' + fieldId)
		const owner = ownerOf(fieldId)
		if (owner) throw new Error(`Field "${ fieldId }" is managed by ${ owner }; use that tool to change it.`)
		return control
	}

	function checkLocale(field: any, locale: string) {
		if (field.localized === false && locale !== ctx.defaultLocale) {
			throw new Error(`Field is not localized; use locale "${ ctx.defaultLocale }".`)
		}
		if (!ctx.getLocaleCodes().includes(locale)) throw new Error('Unknown locale: ' + locale)
	}

	return [
		{
			name: 'get_current_entry',
			definition: {
				name: 'get_current_entry',
				description:
					'Get the currently open entry: its id, content type and field values per locale. ' +
					'Rich text fields are returned as a placeholder — read them with get_rich_text_field. ' +
					'Fields managed by another tool are returned as a placeholder naming that tool.',
				input_schema: { type: 'object', properties: {} },
			},
			handler: () => {
				const entry = ctx.getEntry()
				if (!entry) throw new Error('No entry is currently open.')
				const ct = ctx.getContentType()
				const controls = ctx.getControlsMerged() ?? []
				const rteFields = new Set(controls.filter(c => fieldKind(c.field) === 'richtext').map(c => c.field.id))
				const fields: any = {}
				for (const [ fid, byLocale ] of Object.entries(entry.fields ?? {})) {
					const owner = ownerOf(fid)
					if (owner) {
						fields[fid] = `<managed by ${ owner }>`
					}
					else if (rteFields.has(fid)) {
						fields[fid] = {}
						for (const loc of Object.keys((byLocale as any) ?? {})) {
							fields[fid][loc] = '<rich text; read with get_rich_text_field>'
						}
					}
					else {
						fields[fid] = byLocale
					}
				}
				return { id: entry.sys?.id, content_type: ct?.sys?.id, fields }
			},
		},
		{
			name: 'list_content_fields',
			definition: {
				name: 'list_content_fields',
				description:
					'List the fields of the current entry\'s content type and the available locales. ' +
					'Use "kind" to pick the write tool: kind="text" -> set_field, kind="number" -> set_number_field, ' +
					'kind="boolean" -> set_boolean_field, kind="date" -> set_date_field, ' +
					'kind="list" -> set_list_field, kind="richtext" -> get_rich_text_field / set_rich_text_field (Markdown). ' +
					'kind="other" fields (references, json, ...) cannot be edited yet. ' +
					'If allowed_values is present, only those values are valid; "range" gives numeric min/max; ' +
					'"date_format" tells whether a date expects time/timezone.',
				input_schema: { type: 'object', properties: {} },
			},
			handler: () => {
				const controls = ctx.getControlsMerged() ?? []
				return {
					locales: ctx.getLocaleCodes(),
					default_locale: ctx.defaultLocale,
					fields: controls.map(c => {
						const owner = ownerOf(c.field.id)
						const kind = fieldKind(c.field)
						const allowed = allowedValues(c.field)
						const range = numberRange(c.field)
						return {
							id: c.field.id,
							name: c.field.name,
							type: c.field.type,
							item_type: c.field.items?.type,
							localized: !!c.field.localized,
							kind,
							editable_by_ai: kind !== 'other' && !owner,
							...(owner ? { managed_by: owner } : {}),
							...(allowed ? { allowed_values: allowed } : {}),
							...(range ? { range } : {}),
							...(kind === 'date' ? { date_format: dateFormat(c) } : {}),
						}
					}),
				}
			},
		},
		{
			name: 'set_field',
			definition: {
				name: 'set_field',
				description:
					'Set a single text field (kind="text", type Symbol or Text) of the current entry for a locale. Autosaves. ' +
					'For list fields use set_list_field, for rich text use set_rich_text_field.',
				input_schema: {
					type: 'object',
					properties: {
						field_id: { type: 'string' },
						locale: { type: 'string', description: 'Locale code, e.g. en or de.' },
						value: { type: 'string' },
					},
					required: [ 'field_id', 'locale', 'value' ],
				},
			},
			handler: (input: any) => {
				const { field_id, locale, value } = input ?? {}
				const control = findControl(field_id)
				const kind = fieldKind(control.field)
				if (kind === 'number') throw new Error(`Field "${ field_id }" is numeric; use set_number_field.`)
				if (kind === 'boolean') throw new Error(`Field "${ field_id }" is boolean; use set_boolean_field.`)
				if (kind === 'date') throw new Error(`Field "${ field_id }" is a date; use set_date_field.`)
				if (kind === 'list') throw new Error(`Field "${ field_id }" is a list; use set_list_field with an array of values.`)
				if (kind === 'richtext') throw new Error(`Field "${ field_id }" is rich text; use set_rich_text_field with Markdown.`)
				if (kind !== 'text') throw new Error(`Field "${ field_id }" is of type ${ control.field.type }, which the AI cannot edit yet.`)
				checkLocale(control.field, locale)
				const allowed = allowedValues(control.field)
				if (allowed && !allowed.includes(value)) {
					throw new Error(`"${ value }" is not allowed for "${ field_id }". Allowed values: ${ allowed.join(', ') }.`)
				}
				ctx.setField(field_id, locale, value)
				return `Set ${ field_id } [${ locale }].`
			},
		},
		{
			name: 'set_number_field',
			definition: {
				name: 'set_number_field',
				description:
					'Set a numeric field (kind="number", type Integer or Number) of the current entry for a locale. Autosaves. ' +
					'Integer fields require a whole number. Respect the field\'s "range" (min/max) and "allowed_values" if present.',
				input_schema: {
					type: 'object',
					properties: {
						field_id: { type: 'string' },
						locale: { type: 'string', description: 'Locale code, e.g. en or de.' },
						value: { type: 'number' },
					},
					required: [ 'field_id', 'locale', 'value' ],
				},
			},
			handler: (input: any) => {
				const { field_id, locale, value } = input ?? {}
				const control = findControl(field_id)
				const kind = fieldKind(control.field)
				if (kind === 'text') throw new Error(`Field "${ field_id }" is text; use set_field.`)
				if (kind !== 'number') throw new Error(`Field "${ field_id }" is of type ${ control.field.type }, which is not numeric.`)
				checkLocale(control.field, locale)
				const num = typeof value === 'number' ? value : Number(value)
				if (!Number.isFinite(num)) throw new Error('value must be a number.')
				if (control.field.type === 'Integer' && !Number.isInteger(num)) {
					throw new Error(`Field "${ field_id }" is an integer; value must be a whole number.`)
				}
				const range = numberRange(control.field)
				if (range) {
					if (range.min != null && num < range.min) throw new Error(`${ num } is below the minimum ${ range.min } for "${ field_id }".`)
					if (range.max != null && num > range.max) throw new Error(`${ num } is above the maximum ${ range.max } for "${ field_id }".`)
				}
				const allowed = allowedValues(control.field)
				if (allowed && !allowed.map(Number).includes(num)) {
					throw new Error(`${ num } is not allowed for "${ field_id }". Allowed values: ${ allowed.join(', ') }.`)
				}
				ctx.setField(field_id, locale, num)
				return `Set ${ field_id } [${ locale }] to ${ num }.`
			},
		},
		{
			name: 'set_boolean_field',
			definition: {
				name: 'set_boolean_field',
				description: 'Set a boolean field (kind="boolean") of the current entry for a locale. Autosaves.',
				input_schema: {
					type: 'object',
					properties: {
						field_id: { type: 'string' },
						locale: { type: 'string', description: 'Locale code, e.g. en or de.' },
						value: { type: 'boolean' },
					},
					required: [ 'field_id', 'locale', 'value' ],
				},
			},
			handler: (input: any) => {
				const { field_id, locale, value } = input ?? {}
				const control = findControl(field_id)
				if (fieldKind(control.field) !== 'boolean') throw new Error(`Field "${ field_id }" is of type ${ control.field.type }, not boolean.`)
				checkLocale(control.field, locale)
				let bool: boolean
				if (typeof value === 'boolean') bool = value
				else if (value === 'true') bool = true
				else if (value === 'false') bool = false
				else throw new Error('value must be true or false.')
				ctx.setField(field_id, locale, bool)
				return `Set ${ field_id } [${ locale }] to ${ bool }.`
			},
		},
		{
			name: 'set_date_field',
			definition: {
				name: 'set_date_field',
				description:
					'Set a date field (kind="date") of the current entry for a locale. Autosaves. ' +
					'Accepts a date "YYYY-MM-DD" or an ISO date-time; it is stored in the field\'s configured ' +
					'format (see date_format from list_content_fields). Omitted time defaults to 00:00, ' +
					'omitted timezone to UTC.',
				input_schema: {
					type: 'object',
					properties: {
						field_id: { type: 'string' },
						locale: { type: 'string', description: 'Locale code, e.g. en or de.' },
						value: { type: 'string', description: 'YYYY-MM-DD or an ISO 8601 date-time.' },
					},
					required: [ 'field_id', 'locale', 'value' ],
				},
			},
			handler: (input: any) => {
				const { field_id, locale, value } = input ?? {}
				const control = findControl(field_id)
				if (fieldKind(control.field) !== 'date') throw new Error(`Field "${ field_id }" is of type ${ control.field.type }, not a date.`)
				checkLocale(control.field, locale)
				const normalized = normalizeDate(value, dateFormat(control))
				ctx.setField(field_id, locale, normalized)
				return `Set ${ field_id } [${ locale }] to ${ normalized }.`
			},
		},
		{
			name: 'set_list_field',
			definition: {
				name: 'set_list_field',
				description:
					'Set a list field (kind="list", an Array of Symbol/Text values) of the current entry for a locale. ' +
					'Replaces the whole list with the given values and autosaves. To add to an existing list, ' +
					'read the current values with get_current_entry first and include them.',
				input_schema: {
					type: 'object',
					properties: {
						field_id: { type: 'string' },
						locale: { type: 'string', description: 'Locale code, e.g. en or de.' },
						values: { type: 'array', items: { type: 'string' } },
					},
					required: [ 'field_id', 'locale', 'values' ],
				},
			},
			handler: (input: any) => {
				const { field_id, locale, values } = input ?? {}
				const control = findControl(field_id)
				const kind = fieldKind(control.field)
				if (kind === 'text') throw new Error(`Field "${ field_id }" is a single value; use set_field.`)
				if (kind !== 'list') {
					const detail = control.field.items?.linkType
						? `a list of ${ control.field.items.linkType } references`
						: `type ${ control.field.type }`
					throw new Error(`Field "${ field_id }" is ${ detail }, which the AI cannot edit yet (text lists only).`)
				}
				if (!Array.isArray(values)) throw new Error('values must be an array of strings.')
				checkLocale(control.field, locale)
				const clean = values.map((v: any) => String(v))
				const allowed = allowedValues(control.field)
				if (allowed) {
					const bad = clean.filter(v => !allowed.includes(v))
					if (bad.length) throw new Error(`Not allowed for "${ field_id }": ${ bad.join(', ') }. Allowed values: ${ allowed.join(', ') }.`)
				}
				ctx.setField(field_id, locale, clean)
				return `Set ${ field_id } [${ locale }] to ${ clean.length } item(s).`
			},
		},
		{
			name: 'get_rich_text_field',
			definition: {
				name: 'get_rich_text_field',
				description:
					'Read a rich text field (kind="richtext") of the current entry for a locale as Markdown.',
				input_schema: {
					type: 'object',
					properties: {
						field_id: { type: 'string' },
						locale: { type: 'string', description: 'Locale code, e.g. en or de.' },
					},
					required: [ 'field_id', 'locale' ],
				},
			},
			handler: (input: any) => {
				const { field_id, locale } = input ?? {}
				const control = findControl(field_id)
				if (fieldKind(control.field) !== 'richtext') throw new Error(`Field "${ field_id }" is not a rich text field.`)
				checkLocale(control.field, locale)
				const value = ctx.getEntry()?.fields?.[field_id]?.[locale]
				const markdown = value?.nodeType === 'document' ? documentToMarkdown(value).content : ''
				return { field_id, locale, markdown }
			},
		},
		{
			name: 'set_rich_text_field',
			definition: {
				name: 'set_rich_text_field',
				description:
					'Set a rich text field (kind="richtext") of the current entry for a locale from Markdown. ' +
					'The Markdown is converted to the Contentful rich text format and autosaved. ' +
					'Replaces the whole field; read the current Markdown with get_rich_text_field first if you want to edit it. ' +
					'Supports headings, bold, italic, lists, links, blockquotes and horizontal rules.',
				input_schema: {
					type: 'object',
					properties: {
						field_id: { type: 'string' },
						locale: { type: 'string', description: 'Locale code, e.g. en or de.' },
						markdown: { type: 'string', description: 'The new content as Markdown.' },
					},
					required: [ 'field_id', 'locale', 'markdown' ],
				},
			},
			handler: async (input: any) => {
				const { field_id, locale, markdown } = input ?? {}
				const control = findControl(field_id)
				if (fieldKind(control.field) !== 'richtext') throw new Error(`Field "${ field_id }" is not a rich text field; use set_field or set_list_field.`)
				checkLocale(control.field, locale)
				const doc = await richTextFromMarkdown(String(markdown ?? ''))
				ctx.setField(field_id, locale, doc)
				return `Set rich text ${ field_id } [${ locale }].`
			},
		},
	]
}
